import type {
  IOrthoNeedsFilter,
  TOrthoNeeds,
  TOrthoNeedsMarkType,
} from "@/api/models/patient/orthoNeeds";
import OrthoNeedsService from "@/api/models/patient/orthoNeeds";
import {
  useGenericListQuery,
  useGenericQuery,
  createQueryKeys,
  type TItemQueryBehaviorOptions,
} from "@/composables/shared/queries";
import { type MaybeRefOrGetter } from "vue";
import type { TItemQueryComposableParams } from "@/composables/shared/filters/useGeneralFilter";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";
import { PatientFilesQuerykey } from "./usePatientFiles";

// Re-exported from the API types, where it now lives so schema modules can import it without pulling
// in this file's query-key setup. Kept here so existing import sites keep working.
export { EArchesToTreat } from "@/api/models/patient/orthoNeeds/types";

export const OrthoNeedsQueryKey = createQueryKeys(OrthoNeedsService.queryKey, (key) => ({
  listOfPatientOrthoNeeds: key<IOrthoNeedsFilter>("list"),
  orthoNeedsDetail: key<string>("detail"),
}));

export const usePatientOrthoNeeds = (
  filters: MaybeRefOrGetter<Partial<IOrthoNeedsFilter>>,
  enabled: MaybeRefOrGetter<boolean> = true
) =>
  useGenericListQuery<TOrthoNeeds, Partial<IOrthoNeedsFilter>>({
    queryKey: OrthoNeedsQueryKey.listOfPatientOrthoNeeds(),
    params: { filters, enabled },
    fetcher: (f) => OrthoNeedsService.getAll({}, f),
    permission: $permSubjects.PATIENT__ORTHONEEDSPATIENT,
  });

// export function usePatientOrthoNeeds(id: MaybeRef<string>, enabled: boolean = true) {
//   const refId = toRef(id);
//   const { can } = useAbility();
//   return useQuery({
//     queryKey: computed(() => ["patientOrtNeeds", refId.value]),
//     queryFn: async () => {
//       const response = await OrthoNeedsService.getAll(refId.value);
//       return response.data;
//     },
//     refetchOnWindowFocus: true,

//     enabled: computed(
//       () => enabled && can($permActions.READ, $permSubjects.PATIENT__ORTHONEEDSPATIENT)
//     ),
//   });
// }

export function useOrthoNeeds(
  params: TItemQueryComposableParams<string>,
  options: TItemQueryBehaviorOptions = {}
) {
  return useGenericQuery<TOrthoNeeds, string>({
    queryKey: OrthoNeedsQueryKey.orthoNeedsDetail(),
    params,
    fetcher: (id) => OrthoNeedsService.get({ id }),
    permission: $permSubjects.PATIENT__ORTHONEEDSPATIENT,
    ...options,
  });
}

export function useCreateOrthoNeeds(veeSetErrors?: any) {
  return useGenericMutation<TOrthoNeeds, { orthoNeeds: Partial<TOrthoNeeds> }>({
    mutationFn: ({ orthoNeeds }) => OrthoNeedsService.create({}, orthoNeeds),
    feature: "orthoNeeds-create",
    invalidates: (vars) => [
      OrthoNeedsQueryKey.all(),
      PatientFilesQuerykey.listOfPatientFiles({ patient_id: vars.orthoNeeds.patient }),
    ],
    params: { veeSetErrors },
  });
}

export function useUpdateOrthoNeeds(veeSetErrors?: any) {
  return useGenericMutation<TOrthoNeeds, { orthoNeeds: Partial<TOrthoNeeds> & { id: string } }>({
    mutationFn: ({ orthoNeeds }) => OrthoNeedsService.update({ id: orthoNeeds.id }, orthoNeeds),
    feature: "orthoNeeds-update",
    invalidates: (vars) => [
      OrthoNeedsQueryKey.orthoNeedsDetail(vars.orthoNeeds.id),
      OrthoNeedsQueryKey.listOfPatientOrthoNeeds({ patient: vars.orthoNeeds.patient }),
      PatientFilesQuerykey.listOfPatientFiles({ patient_id: vars.orthoNeeds.patient }),
    ],
    params: { veeSetErrors },
  });
}

export function useOrthoNeedsMarkMutation(veeSetErrors?: any) {
  return useGenericMutation<TOrthoNeeds, { type: TOrthoNeedsMarkType; id: string }>({
    mutationFn: ({ type, id }) => {
      if (!id) return Promise.resolve({} as TOrthoNeeds);
      return OrthoNeedsService.markAs({ id, type }, {});
    },
    feature: "orthoNeeds-mark-mutation",
    params: { veeSetErrors },
  });
}
